// src/components/settings/email-templates-picker.jsx
import React, { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { TEMPLATE_IDS, TEMPLATE_META } from "../../utils/emailTemplateLoader";

const CATEGORY_ORDER = [
  "Open House",
  "Client Touchpoints",
  "Post-Acceptance",
  "Marketing",
  "Vendor Notifications",
];

// Small color chip per category — visible in both grouped + search views.
const CATEGORY_STYLES = {
  "Open House": "bg-sky-100 text-sky-700",
  "Client Touchpoints": "bg-amber-100 text-amber-700",
  "Post-Acceptance": "bg-rose-100 text-rose-700",
  Marketing: "bg-violet-100 text-violet-700",
  "Vendor Notifications": "bg-emerald-100 text-emerald-700",
};

export default function EmailTemplatesPicker({
  selectedId,
  onSelect,
  modifiedIds = [],
}) {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All"); // "All" | category name

  // Flatten + decorate every template once.
  const allTemplates = useMemo(() => {
    return TEMPLATE_IDS.map((id) => {
      const meta = TEMPLATE_META[id];
      if (!meta) return null;
      return { id, ...meta };
    }).filter(Boolean);
  }, []);

  // Apply category chip + free-text search filter.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allTemplates.filter((t) => {
      if (activeCategory !== "All" && t.category !== activeCategory) {
        return false;
      }
      if (!q) return true;
      const hay = [t.label, t.meta, t.category, t.id]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [allTemplates, query, activeCategory]);

  // Category counts for the chip row (respect the search query so counts shrink
  // as the user narrows down).
  const counts = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = { All: 0 };
    CATEGORY_ORDER.forEach((c) => (out[c] = 0));
    allTemplates.forEach((t) => {
      if (q) {
        const hay = [t.label, t.meta, t.category, t.id]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return;
      }
      out.All += 1;
      out[t.category] = (out[t.category] || 0) + 1;
    });
    return out;
  }, [allTemplates, query]);

  const isSearching = query.trim().length > 0;

  // Group results when not searching; flat when searching.
  const grouped = useMemo(() => {
    if (isSearching) return null;
    const byCat = {};
    filtered.forEach((t) => {
      if (!byCat[t.category]) byCat[t.category] = [];
      byCat[t.category].push(t);
    });
    return CATEGORY_ORDER.filter((c) => byCat[c]).map((c) => ({
      category: c,
      items: byCat[c].sort((a, b) => a.label.localeCompare(b.label)),
    }));
  }, [filtered, isSearching]);

  const flatSorted = useMemo(() => {
    return [...filtered].sort((a, b) => a.label.localeCompare(b.label));
  }, [filtered]);

  return (
    <div className="border-r border-slate-200 bg-white flex flex-col overflow-hidden">
      {/* Search */}
      <div className="px-3 pt-3 pb-2">
        <div className="relative">
          <Search
            size={13}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search templates…"
            className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-400 rounded-md pl-7 pr-7 py-1.5 text-[12.5px] text-slate-900 placeholder:text-slate-400 outline-none transition"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
              aria-label="Clear search"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Category filter chips */}
      <div className="px-3 pb-2 flex flex-wrap gap-1">
        {["All", ...CATEGORY_ORDER].map((c) => {
          const isActive = activeCategory === c;
          const count = counts[c] ?? 0;
          return (
            <button
              key={c}
              type="button"
              onClick={() => setActiveCategory(c)}
              className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full border transition ${
                isActive
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {c}
              <span
                className={`ml-1 ${
                  isActive ? "text-slate-300" : "text-slate-400"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="border-t border-slate-100" />

      {/* Results */}
      <div className="flex-1 overflow-y-auto py-2">
        {filtered.length === 0 && (
          <div className="px-4 py-6 text-center text-[12px] text-slate-500">
            No templates match "{query}"
          </div>
        )}

        {isSearching
          ? flatSorted.map((t) => (
              <TemplateRow
                key={t.id}
                t={t}
                isSelected={t.id === selectedId}
                isModified={modifiedIds.includes(t.id)}
                onSelect={onSelect}
                showCategoryBadge
              />
            ))
          : grouped?.map((g) => (
              <div key={g.category} className="mb-2">
                <div className="px-4 pb-1 pt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
                  <span>{g.category}</span>
                  <span className="text-slate-300">{g.items.length}</span>
                </div>
                {g.items.map((t) => (
                  <TemplateRow
                    key={t.id}
                    t={t}
                    isSelected={t.id === selectedId}
                    isModified={modifiedIds.includes(t.id)}
                    onSelect={onSelect}
                  />
                ))}
              </div>
            ))}
      </div>
    </div>
  );
}

function TemplateRow({ t, isSelected, isModified, onSelect, showCategoryBadge }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(t.id)}
      className={`block w-full text-left px-4 py-2 text-sm border-l-2 transition ${
        isSelected
          ? "bg-slate-100 border-slate-800 font-semibold text-slate-900"
          : "border-transparent text-slate-600 hover:bg-slate-50"
      }`}
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        <span>{t.label}</span>
        {isModified && (
          <span className="text-[9px] uppercase font-semibold tracking-wide text-indigo-600">
            edited
          </span>
        )}
        {!t.auto && (
          <span className="text-[9px] uppercase font-semibold tracking-wide text-slate-400">
            manual
          </span>
        )}
        {showCategoryBadge && (
          <span
            className={`ml-auto text-[9px] uppercase font-semibold tracking-wide px-1.5 py-0.5 rounded ${
              CATEGORY_STYLES[t.category] || "bg-slate-100 text-slate-600"
            }`}
          >
            {t.category}
          </span>
        )}
      </div>
      <div className="text-[10px] text-slate-400 mt-0.5">{t.meta}</div>
    </button>
  );
}
